/* The visible Shopify page hours are the sole schedule for this chip.
 * Evaluate against New York time, never the visitor's timezone or Liquid's
 * cached clock. Unrecognized schedules leave the chip hidden, not guessing. */
const LOCATION_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function parseLocationHours(text) {
  const dayIndex = name => LOCATION_WEEK.findIndex(d => d.toLowerCase().startsWith(name.trim().toLowerCase()));
  const time = value => {
    const match = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i.exec(value.trim());
    if (!match) throw new Error('Invalid time');
    let hour = Number(match[1]);
    const minute = Number(match[2] || 0), period = match[3]?.toLowerCase();
    if (minute > 59 || (period ? hour < 1 || hour > 12 : !match[2] || hour > 23)) throw new Error('Invalid time');
    if (period) hour = hour % 12 + (period === 'pm' ? 12 : 0);
    return hour * 60 + minute;
  };
  const intervals = [], seen = new Set();
  const lines = text.replace(/[–—]/g, '-').replace(/\u00a0/g, ' ').split(/[\n;]+/).map(s => s.trim()).filter(Boolean);
  if (!lines.length) throw new Error('Missing hours');
  for (const line of lines) {
    const match = /^([a-z,\s&-]+):?\s+(closed|\d.*)$/i.exec(line);
    if (!match) throw new Error('Unrecognized hours line');
    const days = [];
    if (/^(daily|every day|everyday)$/i.test(match[1].trim())) days.push(0,1,2,3,4,5,6);
    else for (const part of match[1].split(/,|&/)) {
      const range = part.trim().split(/\s*-\s*/);
      if (range.length > 2 || range.some(d => !/^(sun(day)?|mon(day)?|tue(s(day)?)?|wed(nesday)?|thu(r(s(day)?)?)?|fri(day)?|sat(urday)?)$/i.test(d))) throw new Error('Invalid days');
      const first = dayIndex(range[0]), last = dayIndex(range.at(-1));
      if (first < 0 || last < 0) throw new Error('Invalid days');
      for (let d=first;;d=(d+1)%7) { days.push(d); if(d===last) break; }
    }
    for (const d of days) { if (seen.has(d)) throw new Error('Conflicting days'); seen.add(d); }
    if (/^closed$/i.test(match[2])) continue;
    const slots = match[2].split(/\s*,\s*/).map(slot => {
      const pair = slot.split(/\s*-\s*/);
      if(pair.length!==2) throw new Error('Invalid time range');
      const start=time(pair[0]),end=time(pair[1]);
      if(start===end) throw new Error('Ambiguous all-day range');
      return {start,end};
    });
    for (const day of days) for (const slot of slots) {
      intervals.push({start:day*1440+slot.start,end:day*1440+slot.end+(slot.end<slot.start?1440:0)});
    }
  }
  return intervals.sort((a,b)=>a.start-b.start);
}

class LocationHours extends HTMLElement {
  connectedCallback() {
    this.render();
    clearInterval(this.timer);
    this.timer = setInterval(() => this.render(), 60_000);
  }

  disconnectedCallback() { clearInterval(this.timer); }

  now() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const get = t => parts.find(p => p.type === t)?.value;
    return {day: get('weekday'), minutes: Number(get('hour')) % 24 * 60 + Number(get('minute'))};
  }

  displayTime(minutes) {
    const n = ((minutes % 1440) + 1440) % 1440, h = Math.floor(n/60);
    return `${h%12 || 12}:${String(n%60).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
  }

  render() {
    try {
      const source = this.closest('.main-location')?.querySelector('.main-location__hours');
      if (!source) throw new Error('No Shopify hours');
      // Preserve paragraph/list/line-break boundaries and inline rich-text formatting.
      const text = node => node.nodeType === 3 ? node.textContent :
        [...node.childNodes].map(text).join('') + (/^(P|LI|BR|DIV)$/.test(node.nodeName) ? '\n' : '');
      const ranges = parseLocationHours(text(source));
      const {day,minutes} = this.now(), index = LOCATION_WEEK.indexOf(day);
      if(index<0 || !Number.isFinite(minutes)) throw new Error('Invalid clock');
      const at=index*1440+minutes;
      // Include the preceding week's Saturday overnight and next week's openings.
      const windows = [-10080,0,10080].flatMap(offset=>ranges.map(r=>({start:r.start+offset,end:r.end+offset}))).sort((a,b)=>a.start-b.start);
      const current = windows.find(r=>at>=r.start && at<r.end);
      let label;
      if(current) label=`${this.dataset.openLabel} · ${this.dataset.untilLabel} ${this.displayTime(current.end)}`;
      else {
        label=this.dataset.closedLabel;
        const next=windows.find(r=>r.start>at);
        if(next) {
          const daysAway=Math.floor(next.start/1440)-index;
          const dayWord=daysAway>1?` ${LOCATION_WEEK[Math.floor(next.start/1440)%7].slice(0,3)}`:'';
          label+=` · ${this.dataset.opensLabel} ${this.displayTime(next.start)}${dayWord}`;
        }
      }
      this.querySelector('.location-status__label').textContent=label;
      this.dataset.state=current?'open':'closed';
      this.hidden=false;
    } catch {
      this.hidden=true;
      delete this.dataset.state;
    }
  }
}

if (!customElements.get('location-hours')) customElements.define('location-hours', LocationHours);
